import { ChatMessages } from "@storytell/cp-client";
import type { StreamingChatId } from "@storytell/streaming";
import { Show, createEffect, createMemo, createSignal, onCleanup } from "solid-js";
import { useWire } from "~/wire";

const ROTATION_MESSAGES = [
  "Processing your request...",
  "Analyzing information...",
  "Gathering insights...",
  "Searching knowledge base...",
  "Connecting the dots...",
  "Formulating response...",
  "Organizing thoughts...",
  "Reviewing context...",
  "Synthesizing data...",
  "Preparing analysis...",
  "Evaluating options...",
  "Building response...",
  "Checking sources...",
  "Refining answer...",
  "Almost there...",
  "Finalizing response...",
  "Working on it...",
  "Processing...",
  "One moment please...",
  "Computing results...",
  "Exploring possibilities...",
  "Crafting your answer...",
  "Diving deep into details...",
  "Compiling information...",
];

export const HeartbeatIndicator = (props: { chatId: StreamingChatId }) => {
  const wire = useWire();
  const stream = wire.stream;

  const [currentMessageIndex, setCurrentMessageIndex] = createSignal(0);
  const [isVisible, setIsVisible] = createSignal(false);

  // Check if any operations are active (not complete)
  const hasRunningOperations = createMemo(() => {
    // Validate chat ID before proceeding
    if (!props.chatId) {
      return false;
    }

    const chat = stream.chat.get(props.chatId);
    if (!chat || !chat.messages) {
      return false;
    }

    let totalOperations = 0;
    let activeOperations = 0;

    // Check all messages in the chat
    for (const messageId of chat.messages) {
      const message = stream.message.get(messageId);

      // Ensure message exists and has operations array
      if (!message || !message.operations || !Array.isArray(message.operations)) {
        continue;
      }

      // Check all operations in the message
      for (const operationId of message.operations) {
        const operation = stream.operation.get(operationId);

        if (!operation) {
          continue;
        }

        totalOperations++;

        if (!operation.status || typeof operation.status !== "number") {
          continue;
        }

        // Check if the operation is active (not complete) - exclude heartbeat operations
        // Using isComplete field for simpler and more reliable detection (matches OperationHeader pattern)
        if (!operation.isComplete && operation.activity !== "system_heartbeat") {
          activeOperations++;
          return true;
        }
      }
    }

    return false;
  });

  // Effect to track streaming status
  createEffect(() => {
    const isStreaming = hasRunningOperations();

    if (isVisible() !== isStreaming) {
    }

    setIsVisible(isStreaming);

    if (isStreaming) {
      // Reset message index when starting
      setCurrentMessageIndex(0);
    }
  });

  // Effect for rotating messages
  createEffect(() => {
    if (!isVisible()) return;

    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % ROTATION_MESSAGES.length);
    }, 5000); // Rotate every 5 seconds

    onCleanup(() => {
      clearInterval(interval);
    });
  });

  return (
    <Show when={isVisible()}>
      <div
        role="status"
        aria-live="polite"
        aria-label="Processing indicator"
        class="flex items-start justify-between mb-4 animate-in fade-in"
      >
        {/* Main content matching OperationHeader structure exactly */}
        <div class="flex items-start flex-1 gap-4 min-w-0">
          <div class="flex-shrink-0 -mt-0.5">
            {/* Pulsing dot positioned exactly like operation icons */}
            <div class="w-5 h-5 rounded-full relative top-[6px] left-[7px] flex items-center justify-center">
              <div class="relative flex h-2 w-2">
                <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-75" />
                <span class="relative inline-flex h-2 w-2 rounded-full bg-brand" />
              </div>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 group">
              <div class="text-base pt-0.5 font-medium text-muted-text transition-opacity duration-500 ease-in-out">
                {ROTATION_MESSAGES[currentMessageIndex()]}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Show>
  );
};
